import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantStore } from '@/store/tenantStore';
import { useLocalization } from '@/hooks/useLocalization';
import { translateDynamicContent } from '@/i18n';
import { withCurrentCopyrightYear } from '@/utils/copyrightYear';
import { analyticsService } from '@/services/analytics/analyticsService';
import { ROUTES } from '@/app/router/routes';
import { useIsMobile } from '@/hooks/useMediaQuery';
import type { LinkOption, ThemeTextStyle } from '@/types/tenant.types';
import { DynamicFooter, useDynamicConfig } from './DynamicHeaderFooter';
import styles from './AppFooter.module.css';

/**
 * AppFooter — default tenant footer shown on every page.
 *
 * Mirrors Flutter WebPageFooterDefault (used when the tenant has no
 * headerFooterConfig, or it sets defaultFooter):
 *   1. About band — contentSettings.footer.title (upper-cased) + footer.content
 *   2. Links (footer.links, by order) | social icons (contentSettings.socialMedia)
 *      + footer.copyright
 *   3. "Powered by Spire AI" strip
 *
 * The about text, link titles and copyright are tenant-authored, so they are translated through
 * the language file's `dynamicContent` map (Flutter appLocalization.translateMap(dynamicContent)
 * .getValue), not the top-level strings. The title is shown as-is, like Flutter.
 *
 * Colours and text sizes/weights come from themeSettings.footer (background.color falls
 * back to the primary colour, like ColorsConstants.footerBackgroundColor); the font
 * family is always the tenant font.
 *
 * Link behaviour (Flutter): "View all Jobs" navigates in-app, "Help" opens the help
 * dialog, everything else opens its URL.
 */

const VIEW_ALL_JOBS = 'View all Jobs';
const HELP = 'help';

/** Flutter SocialSVGNetworkImage._getIconColor */
const SOCIAL_ICON_COLORS: Record<string, string> = {
  X: '#000000',
  Facebook: '#1877F2',
  LinkedIn: '#0077B5',
};

/**
 * Footer text style — colour and weight from the tenant theme; sizes are fixed regular sizes
 * (the theme's size is a Flutter `.sp` value, which isn't a pixel size). The font stays the
 * tenant font.
 */
function textStyle(
  style: ThemeTextStyle | undefined,
  defaults: { size: number; mobileSize: number; weight: number },
  isMobile: boolean,
): CSSProperties {
  return {
    color: style?.fontColor || '#FFFFFF',
    fontSize: `${isMobile ? defaults.mobileSize : defaults.size}px`,
    fontWeight: Number(style?.fontWeight) || style?.fontWeight || defaults.weight,
  } as CSSProperties;
}

function byOrder(options: LinkOption[] | undefined): LinkOption[] {
  return (options ?? [])
    .filter((o) => o.enable)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Flutter WebPageFooter: the tenant's dynamic footer (headerFooterConfig) unless the brand is
 * Tata, `defaultFooter` is set, or no config is available — then WebPageFooterDefault.
 */
export function AppFooter() {
  const { content, headerFooterConfig } = useTenantStore();
  const footerModel = useDynamicConfig(headerFooterConfig, 'footer');
  const brandName = content?.contentSettings?.general?.name ?? '';
  const useDefault =
    !headerFooterConfig ||
    headerFooterConfig.defaultFooter === true ||
    brandName.toLowerCase().includes('tata');

  if (useDefault) return <DefaultFooter />;
  // Flutter cannot parse a missing config and shows nothing usable either
  if (!footerModel?.config) return null;
  return <DynamicFooter model={footerModel} />;
}

function DefaultFooter() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useLocalization();
  const { content, theme, preferences } = useTenantStore();
  const [showHelp, setShowHelp] = useState(false);

  const settings = content?.contentSettings;
  const footer = settings?.footer;
  const title = footer?.title?.trim() ?? '';
  const about = footer?.content?.trim() ?? '';
  const links = byOrder(footer?.links);
  const socialLinks = byOrder(settings?.socialMedia).filter((s) => s.iconUrl);
  const copyright = footer?.copyright ?? '';

  const footerTheme = theme?.themeSettings?.footer;
  const titleStyle = textStyle(footerTheme?.title, { size: 28, mobileSize: 22, weight: 600 }, isMobile);
  const contentStyle = textStyle(footerTheme?.content, { size: 14, mobileSize: 13, weight: 400 }, isMobile);
  const linkStyle = textStyle(footerTheme?.links, { size: 14, mobileSize: 12, weight: 700 }, isMobile);
  const copyrightStyle = textStyle(footerTheme?.copyright, { size: 13, mobileSize: 12, weight: 400 }, isMobile);

  function handleLinkClick(option: LinkOption) {
    if (option.title === VIEW_ALL_JOBS) {
      // navigateTo(option.linkUrl ?? '/jobs', analyticsClass: 'from_footer_view')
      void analyticsService.logScreenView({
        screenName: option.linkUrl ?? ROUTES.JOBS,
        screenClass: 'from_footer_view',
      });
      navigate(option.linkUrl || ROUTES.JOBS);
      return;
    }
    if (option.title?.toLowerCase() === HELP) {
      setShowHelp(true);
      return;
    }
    analyticsService.logEvent('footer_link', option.title ?? '', `linkUrl: ${option.linkUrl}`);
    if (option.linkUrl) window.open(option.linkUrl, '_blank', 'noopener,noreferrer');
  }

  const prefs = (preferences ?? {}) as Record<string, unknown>;

  return (
    <footer className={styles.footer}>
      {(title || about) && (
        <div className={styles.about}>
          {title && <h2 className={styles.title} style={titleStyle}>{title.toUpperCase()}</h2>}
          {about && <p className={styles.content} style={contentStyle}>{translateDynamicContent(about)}</p>}
        </div>
      )}

      <div className={styles.bar}>
        <div className={styles.barRow}>
          {links.length > 0 && (
            <nav className={styles.links} aria-label={t('Footer')}>
              {links.map((option) => (
                <button
                  key={`${option.order}-${option.title}`}
                  type="button"
                  className={styles.link}
                  style={linkStyle}
                  onClick={() => handleLinkClick(option)}
                >
                  {translateDynamicContent(option.title ?? '')}
                </button>
              ))}
            </nav>
          )}

          {socialLinks.length > 0 && (
            <div className={styles.social}>
              {socialLinks.map((option) => (
                <a
                  key={`${option.order}-${option.title}`}
                  href={option.linkUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialLink}
                  aria-label={option.title}
                  title={option.title}
                  onClick={() => analyticsService.logEvent('footer_link', option.title ?? '', `linkUrl: ${option.linkUrl}`)}
                >
                  {SOCIAL_ICON_COLORS[option.title ?? ''] ? (
                    <span
                      className={styles.socialIconMask}
                      style={{
                        backgroundColor: SOCIAL_ICON_COLORS[option.title ?? ''],
                        WebkitMaskImage: `url("${option.iconUrl}")`,
                        maskImage: `url("${option.iconUrl}")`,
                      }}
                    />
                  ) : (
                    <img src={option.iconUrl} alt="" className={styles.socialIcon} />
                  )}
                </a>
              ))}
            </div>
          )}
        </div>

        {copyright && <p className={styles.copyright} style={copyrightStyle}>{withCurrentCopyrightYear(translateDynamicContent(copyright))}</p>}
      </div>

      <div className={styles.powered}>{t('Powered by Spire AI')}</div>

      {showHelp && (
        <HelpDialog
          title={(prefs['helpDialogTitle'] as string | undefined) ?? 'Need Assistance?'}
          body={(prefs['helpDialogTextBody'] as string | undefined) ?? 'Feel free to reach out to us on: '}
          email={settings?.deleteAccountContent?.emailId ?? ''}
          onClose={() => setShowHelp(false)}
        />
      )}
    </footer>
  );
}

/** Mirrors Flutter CustomDialog.showHelpDialog */
function HelpDialog({ title, body, email, onClose }: {
  title: string;
  body: string;
  email: string;
  onClose: () => void;
}) {
  const { t } = useLocalization();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className={styles.dialogOverlay} onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="footer-help-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.dialogIcon} aria-hidden>?</div>
        <h3 id="footer-help-title" className={styles.dialogTitle}>{t(title)}</h3>
        <p className={styles.dialogBody}>
          {t(body)}
          {email && <a href={`mailto:${email}`} className={styles.dialogEmail}>{email}</a>}
        </p>
        <button type="button" className={styles.dialogBtn} onClick={onClose} autoFocus>
          {t('OK')}
        </button>
      </div>
    </div>
  );
}
